import React, { createContext, useContext, useState } from 'react';
import clsx from 'clsx';

type AccordionContextType = {
  expanded: string | null;
  setExpanded: (value: string | null) => void;
  type: 'single' | 'multiple';
};

const AccordionContext = createContext<AccordionContextType | null>(null);

// Context for passing value from AccordionItem to its children
type AccordionItemContextType = {
  value: string;
};

const AccordionItemContext = createContext<AccordionItemContextType | null>(null);

interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: 'single' | 'multiple';
  collapsible?: boolean;
  defaultValue?: string;
  children: React.ReactNode;
}

export function Accordion({ 
  type = 'single', 
  collapsible = true, 
  defaultValue = '',
  className,
  children,
  ...props 
}: AccordionProps) {
  const [expanded, setExpanded] = useState<string | null>(defaultValue);

  return (
    <AccordionContext.Provider value={{ expanded, setExpanded, type }}>
      <div className={clsx('w-full', className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  children: React.ReactNode;
}

export function AccordionItem({ value, children, className, ...props }: AccordionItemProps) {
  return (
    <AccordionItemContext.Provider value={{ value }}>
      <div className={clsx('border-b', className)} {...props}>
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}

interface AccordionTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

export function AccordionTrigger({ children, className, ...props }: AccordionTriggerProps) {
  const context = useContext(AccordionContext);
  const itemContext = useContext(AccordionItemContext);

  if (!context || !itemContext) return null;

  const isExpanded = context.expanded === itemContext.value;

  const handleClick = () => {
    if (isExpanded) {
      context.setExpanded(null);
    } else {
      context.setExpanded(itemContext.value);
    }
  };

  return (
    <button
      type="button"
      aria-expanded={isExpanded}
      aria-controls={itemContext.value}
      className={clsx(
        'flex w-full items-center justify-between py-4 px-6 text-left',
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {children}
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={clsx(
          'h-4 w-4 shrink-0 transition-transform duration-200',
          isExpanded && 'rotate-180'
        )}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
      </svg>
    </button>
  );
}

interface AccordionContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string; // Optional - will use context if not provided
  children: React.ReactNode;
}

export function AccordionContent({ value, children, className, ...props }: AccordionContentProps) {
  const context = useContext(AccordionContext);
  const itemContext = useContext(AccordionItemContext);

  if (!context) return null;

  // Use provided value or fall back to item context
  const itemValue = value || itemContext?.value;
  if (!itemValue) return null;

  const isExpanded = context.expanded === itemValue;

  return isExpanded ? (
    <div
      id={itemValue}
      className={clsx('overflow-hidden transition-all', className)}
      {...props}
    >
      {children}
    </div>
  ) : null;
} 